/*
  A custom React hook that uses the ResizeObserver API to monitor the dimensions
  of a container element and provides responsive breakpoint information.

  Features:
  - Tracks width, height, aspect ratio, and orientation of the container.
  - Supports custom breakpoints based on size and aspect ratio.
  - Returns a ref to be attached to the target container element.

  Usage:

  ```tsx
  import React from 'react';
  import { useContainerQuery } from './useContainerQuery';

  function MyComponent() {
    const { ref, width, height, orientation, breakpoint } = useContainerQuery<HTMLDivElement>({
      smallSquare: { maxWidth: 399, maxHeight: 399 },
      tallNarrow: { maxWidth: 399, minHeight: 400 },
      wideLow: { minWidth: 400, maxHeight: 299 },
      large: { minWidth: 400, minHeight: 300 }
    })

    return (
      <div ref={ref} className={`container container--${breakpoint}`}>
        <p>Dimensions: {width}x{height}</p>
        <p>Orientation: {orientation}</p>
      </div>
    )
  }
  ```
*/

import { useEffect, useRef, useState } from 'react';

interface ContainerDimensions {
  width: number;
  height: number;
  breakpoint: string;
  orientation: 'portrait' | 'landscape' | 'square';
  aspectRatio: number;
}

interface Breakpoints {
  [key: string]: {
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    minAspectRatio?: number;
    maxAspectRatio?: number;
  };
}

export function useContainerQuery<T extends HTMLElement>(breakpoints?: Breakpoints) {
  const ref = useRef<T>(null);
  const [dimensions, setDimensions] = useState<ContainerDimensions>({
    width: 0,
    height: 0,
    breakpoint: 'default',
    orientation: 'square',
    aspectRatio: 1,
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const aspectRatio = width / height;

        let currentBreakpoint = 'default';
        if (breakpoints) {
          for (const [name, conditions] of Object.entries(breakpoints)) {
            const {
              minWidth = 0,
              maxWidth = Infinity,
              minHeight = 0,
              maxHeight = Infinity,
              minAspectRatio = 0,
              maxAspectRatio = Infinity,
            } = conditions;

            if (
              width >= minWidth &&
              width <= maxWidth &&
              height >= minHeight &&
              height <= maxHeight &&
              aspectRatio >= minAspectRatio &&
              aspectRatio <= maxAspectRatio
            ) {
              currentBreakpoint = name;
              break;
            }
          }
        }

        setDimensions({
          width,
          height,
          breakpoint: currentBreakpoint,
          orientation: aspectRatio > 1 ? 'landscape' : aspectRatio < 1 ? 'portrait' : 'square',
          aspectRatio,
        });
      }
    });

    resizeObserver.observe(element);

    // Cleanup runs on unmount and when breakpoints change
    return () => {
      resizeObserver.disconnect();
    };
  }, [breakpoints]);

  return { ref, ...dimensions };
}
